import React, { useState } from 'react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';

const HoverMenu = ({
	id,
	disabled,
	buttonText,
	buttonIcon,
	buttonIconPosition = 'right',
	position = 'right',
	items,
	menuClassName,
	buttonClassName,
	className,
	onOpenChange
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeItem, setActiveItem] = useState(null);

	// Floating UI setup
	const handleOpenChange = open => {
		setIsOpen(open);
		onOpenChange?.(open);
	};

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: handleOpenChange,
		strategy: 'fixed',
		middleware: [offset(2), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
		placement: position === 'left' ? 'bottom-start' : 'bottom-end'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const handleItemClick = item => {
		if (item.disabled) {
			return;
		}
		if (item.onClick) {
			item.onClick(id);
			setIsOpen(false);
		}
	};

	const handleSubItemClick = subItem => {
		if (subItem.onClick) {
			subItem.onClick(id);
			setIsOpen(false);
		}
	};

	return (
		<div
			className={`hover-menu ${isOpen ? 'is-active' : ''} ${className || ''}`}>
			<button
				className={`hover-menu-button ${buttonClassName || ''}`.trim()}
				ref={refs.setReference}
				disabled={disabled}
				{...getReferenceProps({
					onClick(event) {
						event.stopPropagation();
					}
				})}
				type="button">
				{buttonIconPosition === 'left' ? buttonIcon || <>&#x25BC;</> : null}
				{buttonText ? <span>{buttonText}</span> : null}
				{buttonIconPosition === 'right' ? buttonIcon || <>&#x25BC;</> : null}
			</button>

			<FloatingPortal>
				{isOpen && !disabled && (
					<FloatingFocusManager context={context} modal={false}>
						<ul
							ref={refs.setFloating}
							style={{ ...floatingStyles, zIndex: 9999 }}
							{...getFloatingProps()}
							className={`hover-menu-items ${position} ${menuClassName || ''}`.trim()}>
							{items?.map((item, index) => (
								<li
									key={index}
									className={`hover-menu-item ${item.className || ''} ${
										item.disabled ? 'is-disabled' : ''
									} ${item.tooltip ? 'tooltip is-tooltip-top' : ''}`.trim()}
									data-tooltip={item.tooltip || undefined}
									onMouseEnter={() => setActiveItem(index)}
									onMouseLeave={() => setActiveItem(null)}
									onClick={() => handleItemClick(item)}>
									{item.icon ? <i className={`fas ${item.icon}`} /> : null}
									{item.img ? <img alt={item.label} src={item.img} /> : null}
									{item.label}
									{item.subItems && activeItem === index && (
										<ul className="hover-menu-subitems">
											{item.subItems.map((subItem, subIndex) => (
												<li
													key={subIndex}
													className="hover-menu-subitems-item"
													onClick={e => {
														e.stopPropagation();
														handleSubItemClick(subItem);
													}}>
													{subItem.img ? (
														<img alt={subItem.label} src={subItem.img} />
													) : null}
													{subItem.label}
												</li>
											))}
										</ul>
									)}
								</li>
							))}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
};

export default HoverMenu;
